
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CategoryFormValues } from "@/components/categories/CategoryForm";

// Simple cache to avoid repeated API calls
let productsCache: any[] | null = null;
let productsCacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useProducts() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Check if cache is still valid
  const isCacheValid = useCallback(() => {
    return productsCache && 
           productsCacheTimestamp && 
           (Date.now() - productsCacheTimestamp) < CACHE_DURATION;
  }, []);

  const fetchProducts = useCallback(async (forceRefresh = false) => {
    // Use cache if valid and not forcing refresh
    if (!forceRefresh && isCacheValid()) {
      setProducts(productsCache!);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("products")
        .select(`
          id,
          name,
          description,
          product_prefix,
          image_url,
          factories,
          is_bundle,
          bundle_products,
          sort_order
        `)
        .order("sort_order", { ascending: true })
        .order("name");

      if (error) throw error;
      
      // Ensure factories is always an array of strings
      const productsWithFactories = (data || []).map(product => ({
        ...product,
        factories: Array.isArray(product.factories) ? product.factories : [],
        bundle_products: Array.isArray(product.bundle_products) ? product.bundle_products : [],
        sort_order: product.sort_order || 999
      }));
      
      // Update cache
      productsCache = productsWithFactories;
      productsCacheTimestamp = Date.now();
      
      setProducts(productsWithFactories);
    } catch (error: any) {
      toast({
        title: "Error fetching products",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, isCacheValid]);

  const createProduct = async (values: CategoryFormValues) => {
    try {
      // Create
      const { error } = await supabase
        .from("products")
        .insert({
          name: values.name,
          description: values.description || null,
          product_prefix: values.product_prefix || null,
          image_url: values.image_url || null,
          factories: values.factories || [],
          is_bundle: values.is_bundle,
          bundle_products: values.bundle_products || [],
          sort_order: values.sort_order || 999
        });

      if (error) throw error;
      toast({
        title: "Product Created",
        description: `${values.name} has been created.`
      });
      
      // Clear cache and refresh
      productsCache = null;
      await fetchProducts(true);
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const updateProduct = async (id: string, values: CategoryFormValues) => {
    try {
      // Update
      const { error } = await supabase
        .from("products")
        .update({
          name: values.name,
          description: values.description || null,
          product_prefix: values.product_prefix || null,
          image_url: values.image_url || null,
          factories: values.factories || [],
          is_bundle: values.is_bundle,
          bundle_products: values.bundle_products || [],
          sort_order: values.sort_order || 999,
          updated_at: new Date().toISOString()
        })
        .eq("id", id);

      if (error) throw error;
      toast({
        title: "Product Updated",
        description: `${values.name} has been updated.`
      });
      
      // Clear cache and refresh
      productsCache = null;
      await fetchProducts(true);
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteProduct = async (product: any) => {
    try {
      // Check if product is used in orders
      const { data: orders, error: ordersError } = await supabase
        .from("orders")
        .select("id")
        .eq("product_id", product.id)
        .limit(1);

      if (ordersError) throw ordersError;
      
      if (orders && orders.length > 0) {
        toast({
          title: "Cannot Delete",
          description: "This product is used in orders and cannot be deleted.",
          variant: "destructive",
        });
        return false;
      }

      // If no dependencies, proceed with deletion
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", product.id);

      if (error) throw error;
      
      toast({
        title: "Product Deleted",
        description: `${product.name} has been deleted.`
      });
      
      // Clear cache and refresh
      productsCache = null;
      await fetchProducts(true);
      return true;
    } catch (error: any) {
      toast({
        title: "Error deleting product",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return {
    products,
    loading,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct
  };
}
